<template>
  <v-container class="pa-0 fill-height">
    <v-card v-if="loading" class="fill-height" width="100%" elevation="2">
      <v-card-text class="text-center d-flex flex-column align-center justify-center fill-height">
        <v-progress-circular indeterminate color="primary" />
        <div class="mt-3">{{ $t('loading') }}</div>
      </v-card-text>
    </v-card>

    <v-card v-else class="fill-height" width="100%" elevation="2">
      <v-card-title class="py-3 px-6 d-flex align-center">
        <v-btn
          @click="$router.go(-1)"
          class="mr-2"
          icon
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>

        <span class="text-h5 font-weight-bold justify-start">{{ storyboardData.TITLE || $t('aiwf_sb_form_title') }}</span>

        <v-spacer></v-spacer>

        <v-btn
          color="info"
          small
          @click="handleVisitAssets"
        >
          <v-icon left>mdi-folder</v-icon>
          {{ $t('aiwf_sb_form_visit_assets') }}
        </v-btn>
      </v-card-title>

      <!-- Main Content Area -->
      <v-card-text class="pa-0">
        <v-container fluid class="pa-0 fill-height">
          <v-expansion-panels
            v-model="panel"
            class="fill-height"
            flat
            hover
            multiple
          >
            <v-expansion-panel class="fill-height">
              <v-expansion-panel-header class="py-0 font-weight-bold">
                {{ $t('aiwf_sb_form_film_details') }}
              </v-expansion-panel-header>

              <v-expansion-panel-content class="pa-2">
                <v-row no-gutters class="fill-height">

                  <!-- Visual Mood (Left 1/3) -->
                  <v-col cols="4" class="pr-2">
                    <v-card flat class="fill-height">
                      <v-card-subtitle class="py-0 px-4 font-weight-medium">
                        {{ $t('aiwf_sb_form_visual_mood') }}
                      </v-card-subtitle>
                      <v-card-text class="pa-1">
                        <v-textarea
                          v-model="storyboardData.ESTILO.VALOR"
                          :label="$t('aiwf_sb_form_visual_style_description')"
                          outlined
                          rows="10"
                          dense
                        ></v-textarea>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <!-- Visual Reference (Center 1/3) -->
                  <v-col cols="4" class="px-1">
                    <v-card flat class="fill-height">
                      <v-card-subtitle class="py-0 px-3 font-weight-medium">
                        {{ $t('aiwf_sb_form_visual_reference') }}
                      </v-card-subtitle>
                      <v-card-text class="pa-1">
                        <div class="image-preview-container">
                          <div v-if="!hasVisualReference" class="image-placeholder">
                            <v-icon size="48" color="grey lighten-1">mdi-image-outline</v-icon>
                            <div class="text-caption text-center mt-2">{{ $t('aiwf_sb_form_no_image') }}</div>
                          </div>
                          <div v-else class="image-preview">
                            <v-img :src="visualReferenceUrl" alt="Visual Reference" class="preview-image" contain />
                          </div>
                          <v-btn
                            @click="handleReferenceAction"
                            :elevation="0"
                            class="image-action-btn"
                            color="primary"
                            fab
                            x-small
                          >
                            <v-icon>{{ hasVisualReference ? 'mdi-delete' : 'mdi-upload' }}</v-icon>
                          </v-btn>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <!-- Audio (Right 1/3) -->
                  <v-col cols="4" class="pl-2">
                    <v-card flat class="fill-height">
                      <v-card-subtitle class="py-0 px-3 font-weight-medium">
                        {{ $t('aiwf_sb_form_audio') }}
                      </v-card-subtitle>
                      <v-card-text class="pa-1">

                          <!-- Soundtrack Section -->
                          <div class="mb-4">
                            <StoryboardSoundGenerate
                              :instruction.sync="storyboardData.TRILHA.VALOR"
                              :label="$t('aiwf_sb_form_soundtrack')"
                              :loading="soundtrackGenerating"
                              :nodeId="storyboardData.TRILHA.NODE_ID"
                              :generation-status="storyboardData.TRILHA.STATUS"
                              :rows="2"
                              :outlined="true"
                              @regenerate="regenerateSoundtrack"
                            />
                          </div>

                          <!-- Narrator Persona Section -->
                          <div>
                            <StoryboardSoundGenerate
                              :instruction.sync="storyboardData.PERSONA.VALOR"
                              :label="$t('aiwf_sb_form_narrator_persona')"
                              :loading="personaGenerating"
                              :nodeId="storyboardData.PERSONA.NODE_ID"
                              :generation-status="storyboardData.PERSONA.STATUS"
                              :rows="2"
                              :outlined="true"
                              @regenerate="generatePersonaSample"
                            />
                          </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel class="fill-height">
              <v-expansion-panel-header class="py-0 font-weight-bold">
                {{ $t('aiwf_sb_form_scenes') }}
              </v-expansion-panel-header>

              <v-expansion-panel-content class="pa-2 fill-height">
                <v-container fluid class="pa-0 fill-height">
                  <div class="scenes-horizontal-container">
                    <div class="scenes-horizontal-scroll">
                      <div
                        v-for="(scene, sceneIndex) in storyboardData.CENAS"
                        :key="scene.ID"
                        class="scene-card-wrapper"
                      >
                        <v-card outlined class="scene-card">
                          <v-card-title class="py-2 px-3 grey lighten-3">
                            <span class="text-subtitle-1">{{ $t('aiwf_sb_form_scene') }} {{ scene.ID }}</span>
                          </v-card-title>

                          <!-- Scene Tabs -->
                          <v-tabs v-model="activeTab[sceneIndex]" class="scene-tabs" background-color="grey lighten-3" color="primary">
                            <v-tab>SCRIPT</v-tab>
                            <v-tab :disabled="scene.VOICEOVER.STATUS == 'A APROVAR'">IMAGE</v-tab>
                            <v-tab :disabled="scene.KEYFRAME.STATUS == 'A APROVAR'">VIDEO</v-tab>
                          </v-tabs>

                          <v-card-text class="pa-2 scene-content">
                            <v-tabs-items v-model="activeTab[sceneIndex]" class="fill-height">

                              <!-- SCRIPT Tab -->
                              <v-tab-item class="fill-height">
                                <div class="tab-content">

                                  <v-row class="ma-0 pa-0 justify-space-between align-end">
                                    <v-col cols="9" class="pa-0">
                                      <v-textarea
                                        v-model="scene.KEYFRAME.VALOR"
                                        :label="$t('aiwf_sb_form_scene_description')"
                                        rows="2"
                                        class="mt-3 px-1"
                                        dense
                                      ></v-textarea>
                                    </v-col>

                                    <v-col cols="3" class="pa-0 pl-4">
                                      <v-text-field
                                        v-model="scene.DURACAO"
                                        :label="$t('aiwf_sb_form_duration')"
                                        type="number"
                                        suffix="s"
                                        class="mt-3 px-2"
                                        dense
                                      ></v-text-field>
                                    </v-col>
                                  </v-row>

                                  <!-- Voiceover Section -->
                                  <v-row class="ma-0 pa-0 flex-column">
                                    <StoryboardSoundGenerate
                                      :instruction.sync="scene.VOICEOVER.VALOR"
                                      :label="$t('aiwf_sb_form_voiceover_text')"
                                      :rows="2"
                                      :nodeId="scene.VOICEOVER.NODE_ID"
                                      :loading="scene.voiceoverGenerating"
                                      :has-audio="scene.VOICEOVER && scene.VOICEOVER.NODE_ID !== null && scene.VOICEOVER.NODE_ID !== undefined"
                                      :storyboard-data="storyboardData"
                                      :scene-index="sceneIndex"
                                      :generation-status="scene.VOICEOVER.STATUS"
                                      @regenerate="generateVoiceover(sceneIndex)"
                                    />
                                  </v-row>

                                  <!-- Lettering Row -->
                                  <div class="d-flex align-start pb-4">
                                    <v-textarea
                                      v-model="scene.LETTERING.VALOR"
                                      :label="$t('aiwf_sb_form_lettering')"
                                      rows="2"
                                      dense
                                      class="flex-grow-1 mb-1 px-1"
                                      hide-details
                                    ></v-textarea>
                                  </div>

                                  <v-row class="ma-0 pa-0">
                                    <v-col cols="12">
                                      <v-btn
                                        @click="approveScript(sceneIndex)"
                                        :loading="scene.approvingScript"
                                        :disabled="scene.VOICEOVER.NODE_ID === null || scene.VOICEOVER.NODE_ID === undefined"
                                        color="primary"
                                        block
                                      >
                                        {{ $t('aiwf_sb_form_approve_script') }}
                                      </v-btn>
                                    </v-col>
                                  </v-row>
                                </div>
                              </v-tab-item>

                              <!-- IMAGE Tab -->
                              <v-tab-item class="fill-height">
                                <div class="tab-content">
                                  <StoryboardImageGenerate
                                    :instruction.sync="scene.KEYFRAME.VALOR"
                                    :label="$t('aiwf_sb_form_image_prompt')"
                                    :loading="scene.keyframeGenerating"
                                    :node-id="scene.KEYFRAME.NODE_ID"
                                    :parentId="storyboardData.FOLDER_PARENT_ID"
                                    :storyboard-data="storyboardData"
                                    :scene-index="sceneIndex"
                                    :generation-status="scene.KEYFRAME.STATUS"
                                    @regenerate="generateImage(sceneIndex)"
                                    @uploaded="handleFileUploadKeyframe"
                                    @delete="handleDeleteKeyframe(sceneIndex)"
                                  />

                                  <v-row class="ma-0 pa-0">
                                    <v-col cols="12">
                                      <v-btn
                                        @click="approveImage(sceneIndex)"
                                        :loading="scene.approvingImage"
                                        :disabled="scene.KEYFRAME.NODE_ID === null || scene.KEYFRAME.NODE_ID === undefined"
                                        color="primary"
                                        block
                                      >
                                        {{ $t('aiwf_sb_form_approve_image') }}
                                      </v-btn>
                                    </v-col>
                                  </v-row>
                                </div>
                              </v-tab-item>

                              <!-- VIDEO Tab -->
                              <v-tab-item class="fill-height">
                                <div class="tab-content">

                                  <StoryboardVideoGenerate
                                    :instruction.sync="scene.ANIMATION.VALOR"
                                    :label="$t('aiwf_sb_form_video_prompt')"
                                    :loading="scene.videoGenerating"
                                    :node-id="scene.ANIMATION.NODE_ID"
                                    :parentId="storyboardData.FOLDER_PARENT_ID"
                                    :storyboard-data="storyboardData"
                                    :scene-index="sceneIndex"
                                    :generation-status="scene.ANIMATION.STATUS"
                                    @regenerate="generateVideo(sceneIndex)"
                                  />

                                  <v-row class="ma-0 pa-0">
                                    <v-col cols="12">
                                      <v-btn
                                        @click="approveVideo(sceneIndex)"
                                        :loading="scene.approvingVideo"
                                        color="primary"
                                        block
                                      >
                                        {{ $t('aiwf_sb_form_approve_video') }}
                                      </v-btn>
                                    </v-col>
                                  </v-row>
                                </div>
                              </v-tab-item>

                            </v-tabs-items>
                          </v-card-text>
                        </v-card>
                      </div>

                      <div class="scene-card-wrapper">
                        <div class="scene-card d-flex align-center justify-center flex-column">
                          <v-btn
                            @click="addNewScene"
                            :loading="creatingNewScene"
                            color="primary"
                            outlined
                            fab
                          ><v-icon large>mdi-plus</v-icon>
                          </v-btn>

                          <span class="primary--text text-uppercase font-weight-bold">{{ $t('add_scene') }}</span>
                        </div>
                      </div>

                    </div>
                  </div>
                </v-container>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

        </v-container>
      </v-card-text>
    </v-card>

    <!-- File Upload Modal -->
    <DefaultModal
      @submit="handleFileUpload"
      :title="$t('aiwf_sb_form_upload_visual_reference')"
      submit-button-text="upload"
      ref="fileUploadModal"
    >
      <template v-slot:content>
        <UploadField
          ref="uploadField"
          :rules="[v => (!!v && v.size > 0) || $t('form_rules.mandatory')]"
          :multiple="false"
          :accept="'image/*'"
        />
      </template>
    </DefaultModal>
  </v-container>
</template>

<script>
import Vue from 'vue'
import { mapActions, mapState, mapGetters } from 'vuex'
import StoryboardApi from '@/services/api/ai_workforce/chat'
import DefaultModal from '@/components/DefaultModal.vue'
import UploadField from '@/components/content/default/Form/UploadField.vue'
import StoryboardSoundGenerate from '@/components/ai_workforce/storyboard/StoryboardSoundGenerate.vue'
import StoryboardImageGenerate from '@/components/ai_workforce/storyboard/StoryboardImageGenerate.vue'
import StoryboardVideoGenerate from '@/components/ai_workforce/storyboard/StoryboardVideoGenerate.vue'
import AppActions from '@/store/app/actions-types'
import ContentActions from '@/store/content/actions/actions-types'
import RenditionActions from '@/store/content/rendition/actions-types'
import { BgActionEventBus } from '@/bgActionEventBus'

export default {
  name: 'StoryboardFormView',

  components: {
    DefaultModal,
    UploadField,
    StoryboardVideoGenerate,
    StoryboardSoundGenerate,
    StoryboardImageGenerate,
  },

  data() {
    return {
      loading: false,
      soundtrackGenerating: false,
      personaGenerating: false,
      refNodeId: null,
      visualReferenceUrl: null,
      sessionId: null,
      uploadReferenceFlag: false,
      uploadKeyframeFlag: false,
      creatingNewScene: false,
      sceneIndex: null,
      activeTab: [],
      panel: [0, 1],
      storyboardData: {
        "ID": null,
        "FOLDER_PARENT_ID": "",
        "BRIEFING": {
          "PRODUTO": "",
          "INFORMACOES_MARCA": "",
          "TOM_DE_VOZ": "",
          "PUBLICO": "",
          "PERFIL_DEMOGRAFICO": "",
          "ESTILO_REDACAO": "",
          "ESTILO_VISUAL_BRIEFING": "",
          "REFERENCIA_VISUAL": {
            "NODE_ID": null,
            "VALOR": "",
            "DESCRICAO": null
          },
          "FORMATOS": "",
          "DURACAO_TOTAL": null,
          "PONTOS_ATENCAO": "",
          "OUTRAS_INSTRUCOES": ""
        },
        "ESTILO": {
          "STATUS": "A APROVAR",
          "VALOR": ""
        },
        "PERSONA": {
          "STATUS": "A APROVAR",
          "VALOR": "",
          "EXEMPLO": "",
          "VOICE_ID": "",
          "NODE_ID": null
        },
        "TRILHA": {
          "STATUS": "A APROVAR",
          "VALOR": "",
          "NODE_ID": null
        },
        "CENAS": [
          {
            "ID": 1,
            "NOME": "",
            "DURACAO": 4,
            "KEYFRAME": {
              "STATUS": "A APROVAR",
              "VALOR": "",
              "NODE_ID": null
            },
            "ANIMATION": {
              "STATUS": "A APROVAR",
              "VALOR": "",
              "NODE_ID": null
            },
            "VOICEOVER": {
              "STATUS": "A APROVAR",
              "VALOR": "",
              "NODE_ID": null
            },
            "LETTERING": {
              "STATUS": "A APROVAR",
              "VALOR": ""
            },
            "activeTab": 0,
            "keyframeUrl": null,
            "videoUrl": null,
            "voiceoverGenerating": false,
            "keyframeGenerating": false,
            "videoGenerating": false,
            "approvingScript": false,
            "approvingImage": false,
            "approvingVideo": false
          }
        ]
      }
    }
  },

  mounted() {
    this.initializeStoryboardData()
  },

  watch: {
    'storyboardData.FOLDER_PARENT_ID': {
      handler(parentId, previousParentId) {
        if (previousParentId) {
          BgActionEventBus.$emit('release-room', previousParentId)
        }

        if (parentId) {
          BgActionEventBus.$emit('register-room', parentId)
        }
      },
      immediate: true,
    },

    refNodeId(newNodeId) {
      if (newNodeId) {
        this.getFileReference(newNodeId)
        this.listenForRendition()
      }
    }
  },

  computed: {
    ...mapState('app', {
      selectedAiWorkforceApp: state => state.selectedAiWorkforceApp,
    }),

    ...mapGetters("app", {
      getUserApplicationById: "getUserApplicationById"
    }),

    hasVisualReference() {
      return this.storyboardData.BRIEFING.REFERENCIA_VISUAL.NODE_ID !== null || this.visualReferenceUrl !== null
    },

    agentConfig() {
      const agent = this.getUserApplicationById(this.selectedAiWorkforceApp)

      return this.parseJson(agent.config)
    },
  },

  methods: {
    ...mapActions('app', [
      AppActions.OPEN_APP_INFO_MESSAGE,
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
      AppActions.OPEN_APP_ERROR_MESSAGE
    ]),

    ...mapActions('content/actions', [
      ContentActions.GET_NODE_ACTIONS,
      ContentActions.UPLOAD_FILE,
      ContentActions.CREATE_GDOC,
      ContentActions.CREATE_FOLDER,
      ContentActions.REMOVE,
    ]),

    ...mapActions('content/rendition', [
      RenditionActions.GET_PREVIEW,
    ]),

    handleNotification(message) {
      if (message.type == 'rendition_service' && message.status == 'done' && message.node_id == this.refNodeId) {
        this.getFileReference(this.refNodeId)
        BgActionEventBus.$off('bgAction', this.handleNotification)
      }
    },

    listenForRendition() {
      BgActionEventBus.$off('bgAction', this.handleNotification)
      BgActionEventBus.$on('bgAction', this.handleNotification)
    },

    async initializeStoryboardData() {
      try {
        this.loading = true

        if (this.$route.query.sessionId) {
          this.sessionId = this.$route.query.sessionId
        }

        const messageText = `mostre o json do storyboard`

        await this.sendMessage(messageText)

        this.refNodeId = this.storyboardData.BRIEFING.REFERENCIA_VISUAL.NODE_ID || null

      } catch (error) {
        console.error('Error initializing storyboard data:', error)
        this[AppActions.OPEN_APP_ERROR_MESSAGE](error.message)
      } finally {
        this.loading = false
      }
    },

    mergeStoryboardData(defaultData, receivedData) {
      // Deep merge function to combine default structure with received data
      const merged = JSON.parse(JSON.stringify(defaultData)) // Deep clone default data

      // Merge top-level properties
      Object.keys(receivedData).forEach(key => {
        if (receivedData[key] !== null && receivedData[key] !== undefined) {
          if (typeof receivedData[key] === 'object' && !Array.isArray(receivedData[key])) {
            // For objects, merge recursively
            if (merged[key] && typeof merged[key] === 'object') {
              merged[key] = { ...merged[key], ...receivedData[key] }
            } else {
              merged[key] = receivedData[key]
            }
          } else {
            // For primitives and arrays, replace directly
            merged[key] = receivedData[key]
          }
        }
      })

      return merged
    },

    handleVisitAssets() {
      const parentId = this.storyboardData.FOLDER_PARENT_ID

      if (! parentId) {
        this[AppActions.OPEN_APP_ERROR_MESSAGE]('aiwf_sb_form_assets_no_parent_id')
        return
      }

      const routeData = this.$router.resolve({
        name: 'ContentNavigation',
        params: { appId: this.$route.params.contentAppId, parentId }
      })

      window.open(routeData.href, '_blank')
    },

    handleReferenceAction() {
      if (this.hasVisualReference) {
        this.deleteReference()
      } else {
        this.uploadReference()
      }
    },

    async deleteReference() {
      const nodeId = this.storyboardData.BRIEFING.REFERENCIA_VISUAL.NODE_ID

      await this[ContentActions.REMOVE]({ nodeId })

      this.visualReferenceUrl = null
      this.storyboardData.BRIEFING.REFERENCIA_VISUAL.NODE_ID = null

      this.sendMessage(`Eu quero que voce remova o atributo NODE_ID e DESCRICAO do objeto REFERENCIA_VISUAL do json do storyboard.`)
    },

    uploadReference() {
      this.uploadReferenceFlag = true
      this.$refs.fileUploadModal.openModal()
    },

    handleFileUpload() {
      const file = this.$refs.uploadField.getFiles()

      if (file) {
        const reader = new FileReader()
        reader.onload = (imageData) => {
          const parentId = this.storyboardData.FOLDER_PARENT_ID

          this[AppActions.OPEN_APP_INFO_MESSAGE](this.$t('upload_started'))

          this[ContentActions.UPLOAD_FILE]({parentId, file})
            .then((response) => {
              this.visualReferenceUrl = imageData.target.result

              if (this.uploadReferenceFlag) {
                this.handleFileUploadReference(response.nodeId)
              } else if (this.uploadKeyframeFlag) {
                this.handleFileuploadKeyframe(response)
              }
              this[AppActions.OPEN_APP_SUCCESS_MESSAGE]('aiwf_sb_form_image_uploaded')
            })
            .catch((error) => {
              console.error('Error uploading image:', error)
              this[AppActions.OPEN_APP_ERROR_MESSAGE](error.message)
            }
          )
        }
        reader.readAsDataURL(file)
      }

      this.$refs.fileUploadModal.closeModal()
      this.$refs.fileUploadModal.resetForm()
    },

    async handleFileUploadReference(nodeId) {
      const messageText = `Eu quero que voce substitua o atibuto NODE_ID do objeto REFERENCIA_VISUAL do json do storyboard por "${nodeId}". Depois quero que você execute a ferramenta (Img2Txt). O resultado da ferramenta Img2Txt deve ser um texto em portugues que deve ir para o VALOR do objeto REFERENCIA_VISUAL do json do storyboard. Retorne para mim o json completo do storyboard.`
      await this.sendMessage(messageText)
    },

    async getFileReference(nodeId) {
      const result = await this[RenditionActions.GET_PREVIEW](nodeId)

      this.visualReferenceUrl = result[0].url || null
    },

    async handleFileUploadKeyframe({nodeId, sceneIndex}) {
      this.storyboardData.CENAS[sceneIndex].KEYFRAME.NODE_ID = nodeId

      const messageText = `Eu quero que voce substitua ou crie o atributo NODE_ID do objeto KEYFRAME da cena "${sceneIndex + 1}" do json do storyboard com o valor "${nodeId}".`
      await this.sendMessage(messageText)
    },

    async handleDeleteKeyframe(sceneIndex) {
      const nodeId = this.storyboardData.CENAS[sceneIndex].KEYFRAME.NODE_ID

      await this[ContentActions.REMOVE]({ nodeId })

      const scene = this.storyboardData.CENAS[sceneIndex]
      scene.KEYFRAME.NODE_ID = null

      const messageText = `Eu quero que voce remova o atributo NODE_ID do objeto KEYFRAME da cena "${sceneIndex + 1}" do json do storyboard.`
      await this.sendMessage(messageText)

      this[AppActions.OPEN_APP_SUCCESS_MESSAGE](`Image deleted for Scene ${scene.ID}`)
    },

    async regenerateSoundtrack() {
      try {
        this.soundtrackGenerating = true

        // Get the soundtrack textarea content
        const soundtrackContent = this.storyboardData.TRILHA.VALOR || ''

        // Create the message text as specified
        const messageText = `Eu quero que voce substitua o atibuto VALOR do objeto TRILHA do json do storyboard pelo texto "${soundtrackContent}". Depois quero que você gere novamente uma nova trilha com a ferramenta (Create SoundTrack). O resultado da ferramenta Create SoundTrack deve ser um node_id que deve ir para o NODE_ID do objeto TRILHA do json do storyboard. Retorne para mim o json completo do storyboard.`
        await this.sendMessage(messageText)
        this[AppActions.OPEN_APP_SUCCESS_MESSAGE]('aiwf_sb_form_soundtrack_generated')
      } catch (error) {
        console.error('Error generating soundtrack:', error)
        this[AppActions.OPEN_APP_ERROR_MESSAGE](error.message)
      } finally {
        this.soundtrackGenerating = false
      }
    },

    async generatePersonaSample() {
      try {
        this.personaGenerating = true

        // Get the narrator persona textarea content
        const narratorPersonaContent = this.storyboardData.PERSONA.VALOR || ''

        // Create the message text as specified in the task
        const messageText = `Eu quero que voce substitua o atibuto VALOR do objeto PERSONA do storyboard pelo texto "${narratorPersonaContent}". Depois quero que você gere novamente uma nova persona com a ferramenta (Audio Persona). O resultado da ferramenta Audio Persona deve ser um node_id e um voice_id. O voice_id deve ir para atributo VOICE_ID do objeto PERSONA do json do Storyboard. O node_id deve ir para o NODE_ID do objeto PERSONA do json do storyboard. Retorne para mim o json completo do storyboard.`
        await this.sendMessage(messageText)
        this[AppActions.OPEN_APP_SUCCESS_MESSAGE]('aiwf_sb_form_persona_generated')
      } catch (error) {
        console.error('Error generating persona sample:', error)
        this[AppActions.OPEN_APP_ERROR_MESSAGE](error.message)
      } finally {
        this.personaGenerating = false
      }
    },

    getStatusColor(status) {
      switch (status) {
        case 'APROVADO':
          return 'success'
        case 'GERADO':
          return 'info'
        case 'A APROVAR':
          return 'warning'
        case 'REJEITADO':
          return 'error'
        default:
          return 'grey'
      }
    },

    async generateVoiceover(sceneIndex) {
      const scene = this.storyboardData.CENAS[sceneIndex]
      try {
        Vue.set(this.storyboardData.CENAS[sceneIndex], 'voiceoverGenerating', true)

        // Get the text from the voiceover textarea
        const voiceoverContent = scene.VOICEOVER.VALOR || ''

        // Generate the specific Portuguese message as requested
        const messageText = `Eu quero que voce substitua o atibuto VALOR do objeto VOICEOVER do json do storyboard pelo texto "${voiceoverContent}" para a cena ${scene.ID}. Depois quero que você gere novamente o voiceover com a ferramenta (Voiceover). O resultado da ferramenta Voiceover deve ser um node_id que deve ir para o NODE_ID do objeto VOICEOVER da respectiva cena do json do storyboard. Retorne para mim o json completo do storyboard.`
        await this.sendMessage(messageText)
        this[AppActions.OPEN_APP_SUCCESS_MESSAGE](`Voiceover generated for Scene ${scene.ID}`)
      } catch (error) {
        console.error('Error generating voiceover:', error)
        this[AppActions.OPEN_APP_ERROR_MESSAGE](error.message)
      } finally {
        Vue.set(this.storyboardData.CENAS[sceneIndex], 'voiceoverGenerating', false)
      }
    },

    async generateImage(sceneIndex) {
      const scene = this.storyboardData.CENAS[sceneIndex]
      try {
        Vue.set(this.storyboardData.CENAS[sceneIndex], 'keyframeGenerating', true)

        const messageText = `Eu quero que voce substitua o atibuto VALOR do objeto KEYFRAME do json do storyboard pelo texto "${scene.KEYFRAME.VALOR}" para a cena ${scene.ID}. Depois quero que você gere novamente o keyframe com a ferramenta (Txt2Image). O resultado da ferramenta Txt2Image deve ser um node_id que deve ir para o NODE_ID do objeto KEYFRAME da respectiva cena do json do storyboard. Retorne para mim o json completo do storyboard.`
        await this.sendMessage(messageText)

        this[AppActions.OPEN_APP_SUCCESS_MESSAGE](`Image generated for Scene ${scene.ID}`)
      } catch (error) {
        console.error('Error generating image:', error)
        this[AppActions.OPEN_APP_ERROR_MESSAGE](error.message)
      } finally {
        Vue.set(this.storyboardData.CENAS[sceneIndex], 'keyframeGenerating', false)
      }
    },

    async generateVideo(sceneIndex) {
      const scene = this.storyboardData.CENAS[sceneIndex]
      try {
        Vue.set(this.storyboardData.CENAS[sceneIndex], 'videoGenerating', true)

        const messageText = `Eu quero que voce substitua o atibuto VALOR do objeto ANIMATION do json do storyboard pelo texto "${scene.ANIMATION.VALOR}" para a cena ${scene.ID}. Depois quero que você gere novamente o video com a ferramenta (VideoAgent). O resultado da ferramenta VideoAgent deve ser um node_id que deve ir para o NODE_ID do objeto ANIMATION da respectiva cena do json do storyboard. Retorne para mim o json completo do storyboard.`
        await this.sendMessage(messageText)

        this[AppActions.OPEN_APP_SUCCESS_MESSAGE](`Video generated for Scene ${scene.ID}`)
      } catch (error) {
        console.error('Error generating video:', error)
        this[AppActions.OPEN_APP_ERROR_MESSAGE](error.message)
      } finally {
        Vue.set(this.storyboardData.CENAS[sceneIndex], 'videoGenerating', false)
      }
    },

    async approveScript(sceneIndex) {
      try {
        Vue.set(this.storyboardData.CENAS[sceneIndex], 'approvingScript', true)

        const scene = this.storyboardData.CENAS[sceneIndex]
        const messageText = `Aprovar o lettering e voiceover da cena ${scene.ID}. Depois me retorne o json do storyboard`

        await this.sendMessage(messageText)
        this[AppActions.OPEN_APP_SUCCESS_MESSAGE](`Script approved for Scene ${scene.ID}`)
      } catch (error) {
        console.error('Error approving script:', error)
        this[AppActions.OPEN_APP_ERROR_MESSAGE](error.message)
      } finally {
        Vue.set(this.storyboardData.CENAS[sceneIndex], 'approvingScript', false)
      }
    },

    async approveImage(sceneIndex) {
      try {
        Vue.set(this.storyboardData.CENAS[sceneIndex], 'approvingImage', true)

        const scene = this.storyboardData.CENAS[sceneIndex]
        const messageText = `Aprovar o KEYFRAME da cena ${scene.ID}. Depois me retorne o json do storyboard`

        await this.sendMessage(messageText)
        this[AppActions.OPEN_APP_SUCCESS_MESSAGE](`Image approved for Scene ${scene.ID}`)
      } catch (error) {
        console.error('Error approving image:', error)
        this[AppActions.OPEN_APP_ERROR_MESSAGE](error.message)
      } finally {
        Vue.set(this.storyboardData.CENAS[sceneIndex], 'approvingImage', false)
      }
    },

    async approveVideo(sceneIndex) {
      try {
        Vue.set(this.storyboardData.CENAS[sceneIndex], 'approvingVideo', true)

        const scene = this.storyboardData.CENAS[sceneIndex]
        const messageText = `Aprovar o ANIMATION da cena ${scene.ID}. Depois me retorne o json do storyboard`

        await this.sendMessage(messageText)
        this[AppActions.OPEN_APP_SUCCESS_MESSAGE](`Video approved for Scene ${scene.ID}`)
      } catch (error) {
        console.error('Error approving video:', error)
        this[AppActions.OPEN_APP_ERROR_MESSAGE](error.message)
      } finally {
        Vue.set(this.storyboardData.CENAS[sceneIndex], 'approvingVideo', false)
      }
    },

    addNewScene() {
      this. creatingNewScene = true

      const messageText = `Eu quero que você adicione uma nova cena ao storyboard. A nova cena deve ser preenchida como uma continuação da última cena já criada e que ficar ao fim da lista de cenas. Retorne para mim o json completo do storyboard.`

      this.sendMessage(messageText)
        .then(() => {
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE]('New scene added successfully')
        })
        .catch(error => {
          console.error('Error adding new scene:', error)
          this[AppActions.OPEN_APP_ERROR_MESSAGE](error.message)
        })
        .finally(() => {
          this.creatingNewScene = false
        })
    },

    async sendMessage(messageText) {
      // Send message to AI service
      const response = await StoryboardApi.sendMessage({
        chatInput: messageText,
        sessionId: this.sessionId,
        rootNodeId: this.agentConfig.root_node_id,
      })

      let responseText = this.parseResultData(response)
      let updatedStoryboard = this.parseResultToJson(responseText)

      // Update the interface state with the new storyboard JSON
      this.storyboardData = updatedStoryboard
    },

    parseResultData (messageResponse) {
        const responseText = messageResponse.data.text ||
                            messageResponse.data.message ||
                            messageResponse.data.content ||
                            messageResponse.data.response ||
                            messageResponse.data.output ||
                            messageResponse.data[0]?.output || ''

        return responseText
    },

    parseResultToJson (responseText) {
        let storyboardJson = null
        try {

          if (!responseText) {
            throw new Error('No text content found in API response')
          }

          let jsonString = null

          // Method 1: Look for JSON block markers
          const jsonBlockMatch = responseText.match(/```json\s*([\s\S]*?)\s*```/i)
          if (jsonBlockMatch) {
            jsonString = jsonBlockMatch[1].trim()
          }

          // Method 2: Look for curly braces content
          if (!jsonString) {
            const jsonMatch = responseText.match(/\{[\s\S]*\}/g)
            if (jsonMatch && jsonMatch.length > 0) {
              // Take the largest JSON object found
              jsonString = jsonMatch.reduce((a, b) => a.length > b.length ? a : b).trim()
            }
          }

          // Method 3: Try parsing the entire response as JSON
          if (!jsonString) {
            jsonString = responseText.trim()
          }

          if (!jsonString) {
            throw new Error(responseText)
          }

          // Parse the JSON
          try {
            storyboardJson = JSON.parse(jsonString)
          } catch (jsonError) {
            throw new Error(responseText)
          }

          // Validate that we have a valid storyboard structure
          if (!storyboardJson || typeof storyboardJson !== 'object') {
            throw new Error('Parsed JSON is not a valid object: ' + responseText)
          }

        } catch (parseError) {
          console.error('Error parsing JSON from response:', parseError)
          throw new Error(parseError.message)
        }

        return storyboardJson
    },

    parseJson(str)
    {
      try {
        return JSON.parse(str)
      } catch (e) {
      return {}
      }
    },
  },

  beforeDestroy() {
    BgActionEventBus.$off('bgAction', this.handleNotification)

    const parentId = this.storyboardData.FOLDER_PARENT_ID
    if (parentId) {
      BgActionEventBus.$emit('release-room', parentId)
    }
  }
}
</script>

<style lang="scss" scoped>
  .film-details-section {
    height: 33.33%;
    min-height: 300px;
  }

  .scenes-section {
    height: 66.67%;
    min-height: 400px;
  }

  .image-preview-container {
    position: relative;
    height: 286px;
    border: 1px solid #808080;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #fafafa;
  }

  .scene-image {
    height: 150px;
  }

  .tab-content {
    height: 100%;
    overflow-y: auto;
  }

  .scene-content {
    height: 400px;
    overflow: hidden;
  }

  .scene-tabs {
    background-color: #f5f5f5;
  }

  .image-placeholder {
    text-align: center;
    color: #9e9e9e;
  }

  .image-preview {
    width: 100%;
    height: 100%;
    position: relative;
  }

  .preview-image {
    width: 100%;
    height: 100%;
    border-radius: 6px;
  }

  .image-action-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
  }

  .sound-wave-container {
    height: 24px;
    border-radius: 12px;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .sound-wave-svg {
    width: 100%;
    height: 100%;
    border-radius: 12px;
  }

  .wave-active {
    background-color: #000000;
  }

  .wave-inactive {
    background-color: #f5f5f5;
  }

  .scenes-horizontal-container {
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  .scenes-horizontal-scroll {
    display: flex;
    gap: 16px;
    padding: 8px 0;
    overflow-x: auto;
    overflow-y: hidden;
    height: 100%;
    align-items: flex-start;
  }

  .scene-card-wrapper {
    flex: 0 0 auto;
    width: 36px;
    min-width: 360px;
  }

  .scene-card {
    height: 490px;
    width: 100%;
  }

  .fill-height {
    height: 100%;
  }

  /* Custom scrollbar styling */
  .scenes-horizontal-scroll::-webkit-scrollbar {
    height: 8px;
  }

  .scenes-horizontal-scroll::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  .scenes-horizontal-scroll::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }

  .scenes-horizontal-scroll::-webkit-scrollbar-thumb:hover {
    background: #a1a1a1;
  }

  /* Firefox scrollbar styling */
  .scenes-horizontal-scroll {
    scrollbar-width: thin;
    scrollbar-color: #c1c1c1 #f1f1f1;
  }

  @media (max-width: 960px) {
    .film-details-section {
      height: auto;
      min-height: 400px;
    }

    .scenes-section {
      height: auto;
      min-height: 300px;
    }
  }
</style>
